import { type BeaconRequest, parseLinkIdFromPath } from '@smart-link/shared';
import { getAdClickIds } from './ad-click-ids';
import { getApiBase } from './config';
import { sealBody } from './crypto';
import { getDeviceId } from './device-id';
import { flushEventsBeacon } from './events';
import { getBehaviorSnapshot } from './fingerprint';
import { sessionId } from './session';

async function send(body: BeaconRequest) {
  try {
    const url = `${getApiBase()}/api/beacon`;
    // Seal into the AES-GCM envelope; fall back to plaintext only when Web Crypto
    // is missing (ancient browser) — the worker accepts both. NB: encryption is
    // async, so on the pagehide/unload path the sendBeacon below races page
    // teardown; the common case (encrypt resolves within a microtask) still
    // flushes in time.
    const sealed = await sealBody(body);
    const blob = sealed
      ? new Blob([sealed], { type: 'application/octet-stream' })
      : new Blob([JSON.stringify(body)], { type: 'application/json' });
    // navigator.sendBeacon — survives page unload, fire-and-forget.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
      return;
    }
    // Fallback for older browsers
    fetch(url, {
      method: 'POST',
      headers: { 'content-type': blob.type },
      body: blob,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

interface PartialSignals {
  userAgent: string;
  platform?: string;
  languages: string[];
  hardwareConcurrency?: number;
  deviceMemory?: number;
  screenWidth?: number;
  screenHeight?: number;
  devicePixelRatio?: number;
  timezone?: string;
  maxTouchPoints?: number;
  webdriver?: boolean;
  // Behavior snapshot — populated mainly on the pageHide beacon so未验证(秒退)
  // visits still record dwell time + interaction counts (verify never ran for them).
  dwellMs?: number;
  mouseEvents?: number;
  touchEvents?: number;
  scrollEvents?: number;
}

function collectPartial(): PartialSignals {
  const nav = navigator;
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    languages: Array.from(nav.languages ?? []),
    hardwareConcurrency: nav.hardwareConcurrency,
    deviceMemory: (nav as Navigator & { deviceMemory?: number }).deviceMemory,
    screenWidth: screen.width,
    screenHeight: screen.height,
    devicePixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    maxTouchPoints: nav.maxTouchPoints,
    webdriver: nav.webdriver === true,
    ...getBehaviorSnapshot(),
  };
}

let verifyDone = false;
export function markVerifyDone() {
  verifyDone = true;
}

if (typeof window !== 'undefined') {
  // PageView beacon is fired by the inline <head> script for minimum latency.
  // Only send it from here if the inline path didn't run (e.g. crypto missing).
  if (!window.__TC_PAGE_VIEW_SENT) {
    queueMicrotask(() => {
      void send({
        sessionId,
        deviceId: getDeviceId(),
        linkId: parseLinkIdFromPath(window.location.pathname),
        kind: 'pageView',
        ...getAdClickIds(),
        referer: document.referrer || undefined,
        pageHref: window.location.href,
        partialSignals: collectPartial(),
      });
    });
  }

  // On pagehide / visibility=hidden, if verify hasn't completed, flush a final
  // beacon with whatever signals we managed to collect so far.
  const flush = () => {
    if (verifyDone) return;
    void send({
      sessionId,
      deviceId: getDeviceId(),
      linkId: parseLinkIdFromPath(window.location.pathname),
      kind: 'pageHide',
      ...getAdClickIds(),
      referer: document.referrer || undefined,
      pageHref: window.location.href,
      partialSignals: collectPartial(),
    });
    // Phase 5: also flush the buffered interaction events so未验证(秒退 / 后台挂起)
    // visits keep their partial trajectory. Without this the buffer dies with the
    // page — events otherwise only upload at verify / 8s-timeout / redirect, none
    // of which a bounce reaches. Incremental + idempotent, so calling it on both
    // pagehide and visibilitychange is safe. The pageView beacon already created
    // the audit row, so /api/events finds the session.
    // NOTE: unconditional for now (cost: extra event writes for high-volume crawler
    // traffic) — to be gated by a sampling rate / per-link switch later.
    flushEventsBeacon();
  };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
